import { Suspense, lazy, memo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";

// Lazy load all pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load tools
const TextCaseTool = lazy(() => import("./pages/tools/TextCaseTool"));
const WordCounterTool = lazy(() => import("./pages/tools/WordCounterTool"));
const PasswordGeneratorTool = lazy(() => import("./pages/tools/PasswordGeneratorTool"));
const QRGeneratorTool = lazy(() => import("./pages/tools/QRGeneratorTool"));
const EMICalculatorTool = lazy(() => import("./pages/tools/EMICalculatorTool"));
const AgeCalculatorTool = lazy(() => import("./pages/tools/AgeCalculatorTool"));
const TodoTool = lazy(() => import("./pages/tools/TodoTool"));
const NotesTool = lazy(() => import("./pages/tools/NotesTool"));
const ImageCompressTool = lazy(() => import("./pages/tools/ImageCompressTool"));
const UUIDGeneratorTool = lazy(() => import("./pages/tools/UUIDGeneratorTool"));
const Base64Tool = lazy(() => import("./pages/tools/Base64Tool"));
const TextCleanTool = lazy(() => import("./pages/tools/TextCleanTool"));
const PercentageCalculatorTool = lazy(() => import("./pages/tools/PercentageCalculatorTool"));
const GSTCalculatorTool = lazy(() => import("./pages/tools/GSTCalculatorTool"));
const PDFMergeTool = lazy(() => import("./pages/tools/PDFMergeTool"));
const PDFSplitTool = lazy(() => import("./pages/tools/PDFSplitTool"));
const PDFCompressTool = lazy(() => import("./pages/tools/PDFCompressTool"));
const PDFToImageTool = lazy(() => import("./pages/tools/PDFToImageTool"));
const ImageResizeTool = lazy(() => import("./pages/tools/ImageResizeTool"));
const ImageConvertTool = lazy(() => import("./pages/tools/ImageConvertTool"));
const TextToSpeechTool = lazy(() => import("./pages/tools/TextToSpeechTool"));
const ClipboardTool = lazy(() => import("./pages/tools/ClipboardTool"));

// Footer pages
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));

// Configure QueryClient with optimized settings for high traffic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Loading fallback component with proper accessibility
const PageLoader = memo(function PageLoader() {
  return (
    <div 
      className="flex items-center justify-center min-h-[200px]" 
      role="status" 
      aria-live="polite"
      aria-label="Loading page"
    >
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" aria-hidden="true" />
      <span className="sr-only">Loading...</span>
    </div>
  );
});

const App = memo(function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Index />} />
                {/* PDF Tools */}
                <Route path="/tools/pdf-merge" element={<PDFMergeTool />} />
                <Route path="/tools/pdf-split" element={<PDFSplitTool />} />
                <Route path="/tools/pdf-compress" element={<PDFCompressTool />} />
                <Route path="/tools/pdf-to-image" element={<PDFToImageTool />} />
                {/* Image Tools */}
                <Route path="/tools/image-compress" element={<ImageCompressTool />} />
                <Route path="/tools/image-resize" element={<ImageResizeTool />} />
                <Route path="/tools/image-convert" element={<ImageConvertTool />} />
                {/* Text Tools */}
                <Route path="/tools/text-case" element={<TextCaseTool />} />
                <Route path="/tools/word-counter" element={<WordCounterTool />} />
                <Route path="/tools/text-clean" element={<TextCleanTool />} />
                <Route path="/tools/text-to-speech" element={<TextToSpeechTool />} />
                {/* Calculators */}
                <Route path="/tools/emi-calculator" element={<EMICalculatorTool />} />
                <Route path="/tools/age-calculator" element={<AgeCalculatorTool />} />
                <Route path="/tools/percentage-calculator" element={<PercentageCalculatorTool />} />
                <Route path="/tools/gst-calculator" element={<GSTCalculatorTool />} />
                {/* Utility Tools */}
                <Route path="/tools/password-generator" element={<PasswordGeneratorTool />} />
                <Route path="/tools/qr-generator" element={<QRGeneratorTool />} />
                <Route path="/tools/uuid-generator" element={<UUIDGeneratorTool />} />
                <Route path="/tools/base64" element={<Base64Tool />} />
                {/* Mini Tools */}
                <Route path="/tools/todo" element={<TodoTool />} />
                <Route path="/tools/notes" element={<NotesTool />} />
                <Route path="/tools/clipboard" element={<ClipboardTool />} />
                {/* Footer Pages */}
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsConditions />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
});

export default App;
